import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { SelectField } from '@/common/components/Fields/SelectField/SelectField';
import { IconBadge } from '@/common/components/IconBadge/IconBadge';
import { Icon, TIconProps } from '@/common/components/Icons/Icon';
import { LogoAmericanExpress } from '@/common/components/Illustrations/CardBrands/LogoAmericanExpress';
import { LogoDinersClub } from '@/common/components/Illustrations/CardBrands/LogoDinersClub';
import { LogoDiscover } from '@/common/components/Illustrations/CardBrands/LogoDiscover';
import { LogoJCB } from '@/common/components/Illustrations/CardBrands/LogoJCB';
import { LogoMastercard } from '@/common/components/Illustrations/CardBrands/LogoMastercard';
import { LogoUnionPay } from '@/common/components/Illustrations/CardBrands/LogoUnionPay';
import { LogoVisa } from '@/common/components/Illustrations/CardBrands/LogoVisa';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import {
  IEmployeePaymentMethod,
  IEmployeePaymentMethodMetaData,
} from '@/common/integrations/menhir/employee/payments/payments.types';
import { useRouter } from 'expo-router';

export type TPaymentMethodOption = IEmployeePaymentMethod & {
  label: string;
  value: string;
};

export type TPaymentMethodsFieldProps = {
  methods: IEmployeePaymentMethod[];
  selectedMethod?: IEmployeePaymentMethod;
  onSelect: (paymentMethod: IEmployeePaymentMethod) => void;
  labelKey?: string;
};

export function PaymentMethodsField({
  methods,
  selectedMethod,
  onSelect,
  labelKey = 'general.labels.payment_method',
}: TPaymentMethodsFieldProps) {
  const router = useRouter();
  const { t } = useTranslation();

  const options = methods.map((method) => ({
    ...method,
    label: `${method.metadata.brand} •••• •••• •••• ${method.metadata.last4Digits}`,
    value: method.id,
  }));

  const selectedOption = options.find(
    (option) => option.id === selectedMethod?.id
  );

  return (
    <SelectField
      id="payment-method"
      label={t(labelKey)}
      value={selectedOption}
      options={options}
      onChange={(value) => {
        if (value) {
          onSelect(value);
        }
      }}
      renderItem={renderMethodOption}
      footer={
        <Button
          variant="tertiary"
          size="sm"
          onPress={() => {
            router.push(ROUTES.MEAL_VOUCHER_PAYMENT_ADD_CARD);
          }}
        >
          {t('general.action.add_card')}
        </Button>
      }
    />
  );
}

function renderMethodOption(option: TPaymentMethodOption) {
  return (
    <Stack horizontal align="center" grow gap="sm">
      <IconBadge
        icon={cardBrandMapping[option.metadata.brand]}
        ratio={40}
        backgroundColor="neutral30"
      />
      <BodyText bold>•••• •••• •••• {option.metadata.last4Digits}</BodyText>
    </Stack>
  );
}

export const cardBrandMapping: Record<
  IEmployeePaymentMethodMetaData['brand'],
  ReactElement<TIconProps>
> = {
  visa: <LogoVisa width={27} height={10} />,
  mastercard: <LogoMastercard width={22} height={14} />,
  amex: <LogoAmericanExpress width={24} height={24} />,
  diners: <LogoDinersClub width={24} height={24} />,
  discover: <LogoDiscover width={24} height={24} />,
  jcb: <LogoJCB width={24} height={24} />,
  unionpay: <LogoUnionPay width={24} height={24} />,
  unknown: <Icon icon="credit-card" />,
};
